<template>
    <v-dialog v-model="dialog" fullscreen hide-overlay @keydown.esc="close()">
        <v-form
            ref="form"
            v-model="form.valid"
            lazy-validation
        >
            <v-card min-height="100vh" id="formcontract">
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click.native="close()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>{{ form.title }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn
                            dark
                            text
                            :disabled="!form.valid"
                            :loading="btnDisable"
                            @click.native="save(formMethod)"
                        >{{ $t('common.save') }}</v-btn>
                    </v-toolbar-items>
                </v-toolbar>

                <v-container>
                    <v-card-title>
                        <v-subheader>{{ $t('contracts.form.data') }}</v-subheader>
                        <v-spacer></v-spacer>
                        <ia-action-button v-if="contract.file && contract.file.url" :setup="contract"/>
                    </v-card-title>

                    <v-divider></v-divider>

                    <v-progress-linear :indeterminate="true" v-show="form.loading"></v-progress-linear>

                    <div v-show="!form.loading">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="contract.contract"
                                    :label="form.labels.contract"
                                    :rules="form.rules.contract"
                                    prepend-icon="description"
                                    required
                                    filled
                                    dense
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" :md="form.visibility.client.md" v-if="form.visibility.client.show">
                                <input-auto-complete :setup.sync="form.selects.client"></input-auto-complete>
                            </v-col>

                            <v-col cols="12" :md="form.visibility.department.md" v-if="form.visibility.department.show">
                                <input-auto-complete :setup.sync="form.selects.department"></input-auto-complete>
                            </v-col>

                            <template v-if="form.companyType !== 'department'">
                                <v-col cols="12" :md="form.visibility.part.md" v-if="form.visibility.part.show">
                                    <input-auto-complete :setup.sync="form.selects.part"></input-auto-complete>
                                </v-col>

                                <v-col cols="12" :md="form.visibility.typePart.md" v-if="form.visibility.typePart.show">
                                    <v-text-field
                                        v-model="contract.typePart"
                                        :label="form.labels.typePart"
                                        :rules="form.rules.typePart"
                                        prepend-icon="description"
                                        required
                                        filled
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </template>
                        </v-row>

                        <v-row>
                            <template v-if="form.companyType === 'department'">
                                <v-col cols="12" :md="form.visibility.part.md" v-if="form.visibility.part.show">
                                    <input-auto-complete :setup.sync="form.selects.part"></input-auto-complete>
                                </v-col>

                                <v-col cols="12" :md="form.visibility.typePart.md" v-if="form.visibility.typePart.show">
                                    <v-text-field
                                        v-model="contract.typePart"
                                        :label="form.labels.typePart"
                                        :rules="form.rules.typePart"
                                        prepend-icon="description"
                                        required
                                        filled
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </template>

                            <v-col cols="12" :md="form.visibility.validity.md" v-if="form.visibility.validity.show">
                                <div class="d-flex">
                                    <v-switch
                                        inset
                                        dense
                                        hide-details
                                        v-model="form.datePickers.validity.switch"
                                        :label="$t('contracts.form.validity.switch')"
                                        class="mt-3 mr-4"
                                        @change="changeDPState"
                                    ></v-switch>
                                    <input-date-picker :setup.sync="form.datePickers.validity"></input-date-picker>
                                </div>
                            </v-col>

                            <v-col cols="12" :md="form.visibility.status.md" v-if="form.visibility.status.show">
                                <input-select :setup.sync="form.selects.status"></input-select>
                            </v-col>
                        </v-row>

                        <contract-part :setup.sync="form" :contract.sync="contract"></contract-part>

                        <contract-file-upload v-model="contract" :form.sync="form"/>

                        <div class="wrapper-editor-text mt-5">
                            <v-divider class="mb-5"></v-divider>
                            <div class="title mb-3">
                                <v-icon>description</v-icon>
                                {{ $t('contracts.form.observation') }}
                            </div>
                            <editor-observation v-model="contract.objectContractDescription" :color="'#E0E0E0'" :background="true" class="mb-2"/>
                        </div>

                        <contract-deadline :setup.sync="form" :contract.sync="contract"></contract-deadline>
                        <contract-additive :contract.sync="contract"></contract-additive>
                    </div>
                </v-container>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script>
import API from '@/api'
import InputDatePicker from '@/components/InputDatePicker'
import InputSelect from '@/components/InputSelect'
import InputAutoComplete from '@/components/InputAutoComplete'
import ContractPart from '@/components/contract/ContractPart'
import ContractFileUpload from '@/components/contract/ContractFileUpload'
import ContractDeadline from '@/components/contract/ContractDeadline'
import ContractAdditive from '@/components/contract/ContractAdditive'
import IaActionButton from '@/components/chat/IaActionButton'
import EditorObservation from '@/components/EditorObservation'

export default {
    name: 'form-contract',
    metaInfo () {
        if (this.$route.params.id != null) {
            return {
                title: this.$t('contracts.form.metaDetail.title'),
                meta: [
                    { name: 'description', content: this.$t('contracts.form.metaDetail.description') },
                    { name: 'keywords', content: this.$t('contracts.form.metaDetail.keywords') }
                ]
            }
        } else {
            return {
                title: this.$t('contracts.form.metaNew.title'),
                meta: [
                    { name: 'description', content: this.$t('contracts.form.metaNew.description') },
                    { name: 'keywords', content: this.$t('contracts.form.metaNew.keywords') }
                ]
            }
        }
    },
    components: {
        InputDatePicker,
        InputSelect,
        InputAutoComplete,
        ContractPart,
        ContractFileUpload,
        ContractDeadline,
        ContractAdditive,
        IaActionButton,
        EditorObservation
    },
    data: function () {
        return {
            btnDisable: false,
            dialog: true,
            form: {
                loading: true,
                valid: false,
                title: this.$t('contracts.form.titleNew'),
                isEmployee: false,
                companyType: undefined,
                labels: {
                    contract: this.$t('contracts.form.contract.label'),
                    typePart: this.$t('contracts.form.typePart.label'),
                    objectContract: this.$t('contracts.form.objectContract.label')
                },
                rules: {
                    contract: [
                        v => !!v || this.$t('contracts.form.typePart.required'),
                        v => (v && v.length >= 3) || this.$t('contracts.form.typePart.length')
                    ],
                    typePart: [
                        v => !!v || this.$t('contracts.form.typePart.required'),
                        v => (v && v.length >= 3) || this.$t('contracts.form.typePart.length')
                    ],
                    objectContract: [
                        v => !!v || v.size > 0 || this.$t('contracts.form.objectContract.required')
                    ]
                },
                datePickers: {
                    validity: {
                        label: this.$t('contracts.form.validity.label'),
                        rule: [
                            v => !!v || this.$t('contracts.form.validity.required')
                        ],
                        required: true,
                        readonly: false,
                        filled: true,
                        dateFormatted: undefined,
                        switch: false
                    },
                    deadline: []
                },
                selects: {
                    client: {
                        label: this.$t('contracts.form.client.label'),
                        text: 'name',
                        value: 'objectId',
                        icon: 'contacts',
                        required: true,
                        readonly: false,
                        filled: true,
                        multiple: false,
                        rule: [
                            v => !!v || this.$t('contracts.form.client.required')
                        ],
                        items: [],
                        selectedItems: undefined
                    },
                    department: {
                        label: this.$t('contracts.form.department.label'),
                        text: 'name',
                        value: 'objectId',
                        icon: 'contacts',
                        required: false,
                        readonly: false,
                        filled: true,
                        clearable: true,
                        multiple: false,
                        rule: [],
                        items: [],
                        selectedItems: undefined
                    },
                    status: {
                        label: this.$t('contracts.form.status.label'),
                        text: 'label',
                        value: 'value',
                        icon: 'feedback',
                        required: true,
                        readonly: false,
                        filled: true,
                        multiple: false,
                        rule: [
                            v => !!v || this.$t('contracts.form.status.required')
                        ],
                        items: [
                            { value: 1, label: this.$t('contracts.status.items.draft') },
                            { value: 2, label: this.$t('contracts.status.items.signatures') },
                            { value: 3, label: this.$t('contracts.status.items.active') },
                            { value: 4, label: this.$t('contracts.status.items.renewal') },
                            { value: 5, label: this.$t('contracts.status.items.rescinded') },
                            { value: 6, label: this.$t('contracts.status.items.terminated') }
                        ],
                        selectedItems: undefined
                    },
                    part: {
                        label: this.$t('contracts.form.partField.label'),
                        text: 'name',
                        value: 'value',
                        icon: 'description',
                        required: true,
                        multiple: false,
                        clearable: true,
                        filled: true,
                        rule: [
                            v => !!v || this.$t('contracts.form.partField.required')
                        ],
                        items: [],
                        selectedItems: undefined,
                        other: false
                    }
                },
                deadlines: {
                    loading: false,
                    item: undefined
                },
                part: {
                    loading: false,
                    item: {
                        type: undefined,
                        name: undefined,
                        isMainPart: false
                    }
                },
                changeFile: undefined,
                objectContractError: false,
                visibility: {
                    client: {
                        show: false,
                        md: 3
                    },
                    department: {
                        show: false,
                        md: 6
                    },
                    part: {
                        show: false,
                        md: 3
                    },
                    typePart: {
                        show: false,
                        md: 3
                    },
                    validity: {
                        show: true,
                        md: 8
                    },
                    status: {
                        show: true,
                        md: 4
                    }
                }
            },
            contract: {
                objectId: undefined,
                contract: undefined,
                client: undefined,
                validity: undefined,
                status: '0',
                part: [],
                partRemoved: [],
                deadline: [],
                objectContractDescription: '',
                file: undefined,
                additive: [],
                additiveRemoved: []
            }
        }
    },
    watch: {
        'dialog': {
            handler: function (val, oldVal) {
                if (!val) {
                    let root = document.documentElement
                    root.classList.remove('overflow-y-hidden')
                }
            },
            deep: true
        },
        'form.selects.client.selectedItems': {
            handler: function (val) {
                if (this.form.companyType === 'manager') {
                    if (val === this.form.companyId) {
                        this.form.visibility.client = {
                            show: true,
                            md: 3
                        }
                        this.form.visibility.department = {
                            show: true,
                            md: 3
                        }
                    } else {
                        this.form.visibility.client.md = 6
                        this.form.visibility.department.show = false
                    }
                }
            },
            deep: true
        },
        'form.selects.part.selectedItems': {
            handler: function (val) {
                this.contract.typePart = ''
                if (this.form.companyType !== 'department') {
                    if (val === 'Outro') {
                        this.form.visibility.part.md = 3
                        this.form.visibility.typePart = {
                            show: true,
                            md: 3
                        }
                    } else {
                        this.form.visibility.part.md = 6
                        this.form.visibility.typePart.show = false
                    }
                } else {
                    if (val === 'Outro') {
                        this.form.visibility.part.md = 6
                        this.form.visibility.typePart = {
                            show: true,
                            md: 6
                        }
                        this.form.visibility.validity.md = 8
                        this.form.visibility.status.md = 4
                    } else {
                        this.form.visibility.part.md = 12
                        this.form.visibility.validity.md = 8
                        this.form.visibility.status.md = 4
                        this.form.visibility.typePart.show = false
                    }
                }
            },
            deep: true
        }
    },
    mounted () {
        document.getElementById('formcontract').addEventListener('click', this.handleFocus)
        let root = document.documentElement
        root.classList.add('overflow-y-hidden')
        this.configForm()
    },
    computed: {
        formMethod () {
            return (this.$route.params.id != null)
        }
    },
    methods: {
        async configForm () {
            const currentUserCompany = JSON.parse(localStorage.getItem('currentUserCompany'))
            this.form.companyId = currentUserCompany.client.objectId
            this.form.companyType = currentUserCompany.client.type
            this.form.isEmployee = this.$helpers.checkIsEmployee()

            await Promise.all([
                this.getAllDepartment(),
                this.getContractRoles()
            ])

            switch (this.form.companyType) {
            case 'department':
                this.form.visibility.part = {
                    show: true,
                    md: 12
                }
                this.form.visibility.validity.md = 8
                this.form.visibility.status.md = 4
                break
            case 'company':
            case 'managed_company':
                this.form.visibility.department = {
                    show: true,
                    md: 6
                }
                this.form.visibility.part = {
                    show: true,
                    md: 6
                }
                break
            default:
                this.form.visibility.client = {
                    show: true,
                    md: 6
                }
                this.form.visibility.department = {
                    show: false,
                    md: 3
                }
                this.form.visibility.part = {
                    show: true,
                    md: 6
                }
                break
            }

            if (this.$route.params.id != null) {
                this.form.title = this.$t('contracts.form.titleDetail')
                this.loadData()
            } else {
                this.getAllClient()
                if (this.form.companyType === 'company' || this.form.companyType === 'managed_company') {
                    this.form.selects.client.selectedItems = currentUserCompany.client.objectId
                }

                if (this.form.companyType === 'department') {
                    this.form.isEmployee = false
                }

                this.form.loading = false
            }
        },
        close () {
            this.dialog = false
            this.$router.go(-1)
        },
        async getAllClient () {
            this.form.selects.client.items = await this.$helpers.getAllClient(null, false, true)
        },
        async getAllDepartment () {
            this.form.selects.department.items = await this.$helpers.getAllClientTwo(true, 0, 'department')
        },
        async getContractRoles () {
            this.form.selects.part.items = await this.$helpers.getContractRoles()
            this.form.selects.part.items.push({ value: 'Outro', name: 'Outro' })
        },
        loadData () {
            let endpoint = API.EndPoints.getContract
            let params = {
                objectId: this.$route.params.id
            }
            if (!this.form.isEmployee) {
                params.clientId = this.$helpers.getUserCompany().client.objectId
            }
            new API(endpoint, params).request().then(async response => {
                this.form.datePickers.validity.dateFormatted = this.$options.filters.momentFormatDate(response.data.result.validity, 'L')
                this.contract = Object.assign({}, response.data.result)
                this.contract.partRemoved = []
                this.contract.additiveRemoved = []
                this.form.selects.client.items = [this.contract.client]
                this.form.selects.client.selectedItems = this.contract.client.objectId

                if (this.contract.department) {
                    this.form.selects.department.selectedItems = this.contract.department.objectId
                }

                this.form.selects.part.selectedItems = 'Outro'
                const typePart = this.contract.typePart
                const part = this.form.selects.part.items.filter((part) => {
                    return part.value === this.contract.typePart
                })[0].value
                if (part !== undefined) {
                    this.form.selects.part.selectedItems = part
                }
                this.$nextTick(() => {
                    this.contract.typePart = typePart
                })

                if (this.contract.validity === undefined) {
                    this.changeDPState()
                    this.form.datePickers.validity.switch = true
                }

                this.form.selects.status.selectedItems = this.contract.status
                this.form.loading = false
            }, (e) => {
                this.$helpers.requestError(e)
            })
        },
        async save (boolean) {
            this.btnDisable = true
            if (this.$refs.form.validate()) {
                this.form.valid = false
                this.form.loading = true
                if (this.form.changeFile) {
                    if (this.contract.file !== undefined) {
                        this.contract.file = await this.uploadFile(this.contract.file)
                    } else {
                        this.contract.file = { __op: 'Delete' }
                    }
                }
                for (let index = 0; index < this.contract.additive.length; index++) {
                    if (this.contract.additive[index].changeFile) {
                        if (this.contract.additive[index].file !== undefined) {
                            this.contract.additive[index].file = await this.uploadFile(this.contract.additive[index].file)
                        } else {
                            this.contract.additive[index].file = { __op: 'Delete' }
                        }
                    }
                }
                let endpoint = API.EndPoints.saveContract
                new API(endpoint, this.formatObject()).request()
                    .then((response) => {
                        this.saveSuccess()
                    }, (error) => {
                        if (error.status !== 0) this.saveError()
                    })
            } else {
                this.form.objectContractError = true
                this.btnDisable = false
                this.form.valid = true
            }
        },
        async uploadFile (file) {
            let endpoint = API.EndPoints.upload
            endpoint.filename = this.$helpers.normalizeFilename(file.name)
            let fileFormated = await new API(endpoint, file).request()
                .then(response => {
                    let file = response.data
                    file.__type = 'File'
                    return file
                })
            return fileFormated
        },
        formatObject () {
            const userCompany = this.$helpers.getUserCompany()
            if (this.form.isEmployee) {
                this.contract.owner = userCompany.client.objectId

                if (this.form.selects.client.items.length > 0 && this.form.selects.client.selectedItems) {
                    this.contract.client = this.form.selects.client.items.filter((client) => {
                        return client.objectId === this.form.selects.client.selectedItems
                    })[0].objectId
                }

                if (this.form.selects.department.selectedItems) {
                    this.contract.department = this.form.selects.department.items.filter((department) => {
                        return department.objectId === this.form.selects.department.selectedItems
                    })[0].objectId
                }

                if (this.form.companyType !== 'manager') {
                    this.contract.client = userCompany.client.objectId
                }
            } else {
                this.contract.owner = userCompany.client.parent.objectId
                this.contract.client = userCompany.client.parent.objectId
                this.contract.department = userCompany.client.objectId
            }

            if (this.form.selects.part.selectedItems) {
                const part = this.form.selects.part.items.filter((part) => {
                    return part.value === this.form.selects.part.selectedItems
                })[0].value
                if (part !== 'Outro') {
                    this.contract.typePart = part
                }
            }

            this.contract.validity = this.form.datePickers.validity.dateFormatted
            this.contract.status = this.form.selects.status.selectedItems
            this.contract.pendingAdditive = false
            for (let index = 0; index < this.contract.additive.length; index++) {
                if (this.contract.additive[index].status === 1) {
                    this.contract.pendingAdditive = true
                    break
                }
            }
            let params = JSON.stringify(this.contract)
            return params
        },
        saveSuccess () {
            window.getApp.refreshContracts = true
            if (this.$route.params.requesterType !== 'manager') {
                const routers = {
                    [`CadastrarContrato${this.$route.meta.singleName}AA`]: this.$route.params.activeTab,
                    [`DetalheContrato${this.$route.meta.singleName}Ativos`]: { name: `${this.$route.meta.singleName}AbaInternaContratosAtivos`, params: { id: this.$route.params.requesterId } },
                    [`DetalheContrato${this.$route.meta.singleName}Inativos`]: { name: `${this.$route.meta.singleName}AbaInternaContratosInativos`, params: { id: this.$route.params.requesterId } }
                }
                this.$router.push(routers[this.$route.name]).catch(e => {})
            } else {
                this.$router.push('/contratos').catch(e => {})
            }
        },
        saveError () {
            this.btnDisable = false
            alert(this.$t('common.errorSave'))
        },
        handleFocus () {
            this.$eventHub.$emit('handleFocus')
        },
        removeFile () {
            this.contract.file = undefined
        },
        changeDPState () {
            this.form.datePickers.validity.disabled = !this.form.datePickers.validity.disabled
            this.form.datePickers.validity.required = !this.form.datePickers.validity.required
            if (this.form.datePickers.validity.disabled === true) {
                this.form.datePickers.validity.rule = []
            } else {
                this.form.datePickers.validity.rule = [
                    v => !!v || this.$t('contracts.form.validity.dateRequired')
                ]
            }
            this.form.datePickers.validity.dateFormatted = undefined
        }
    }
}
</script>

<style lang="sass" scoped>
    .v-dialog__content.v-dialog__content--active,
    .v-dialog__content.v-dialog__content--active .full-screen
        min-height: 100vh
    .file-info
        .file-name
            max-height: 1.375rem
            overflow: hidden
    .file-info:hover
        background-color: #f6f8f9
</style>
